import React, { useEffect, useState } from "react";
import {
  AvgTd,
  SubTitle,
  SumTd,
  Table,
  TableContainer,
  Td,
  Th,
  Tr,
} from "./HomeStyle";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  LabelList,
  Cell,
} from "recharts";
import {
  getDashBoardMain1,
  getDashBoardMain2,
  getDashBoardMain3,
  getDashBoardMain4,
} from "../../Service/API";

const AdminHome = (props) => {
  const [cuCode, setCuCode] = useState("");
  const [dataAdmin1, setDataAdmin1] = useState([]); // 주간 사육동향
  const [dataAdmin2, setDataAdmin2] = useState(null); // 산차별 착유우 현황
  const [dataAdmin3, setDataAdmin3] = useState(null); // 비유일수별 착유우 현황
  const [dataAdmin4, setDataAdmin4] = useState(null); // 산유량별 착유우 현황

  const [chartDataAdmin2, setChartDataAdmin2] = useState([]); //산차별 비율 차트 데이터
  const [chartDataAdmin4, setChartDataAdmin4] = useState([]); //송아지 사육현황 비율 차트 데이터

  const tempDay = new Date();
  tempDay.setDate(tempDay.getDate() - 1);
  const [yesterday, setYesterday] = useState(tempDay);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [maxAdminSupp, setMaxAdminSupp] = useState(0);
  const [maxAdminCow, setMaxAdminCow] = useState(0);
  // 초기 실행
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        const storedUserId = localStorage.getItem("userId");
        await fetchAdminData();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [cuCode]);

  // API 호출 함수 (직원)
  const fetchAdminData = async (cuCode) => {
    try {
      setLoading(true);
      const [resultAdmin1, resultAdmin2, resultAdmin3, resultAdmin4] =
        await Promise.all([
          getDashBoardMain1(),
          getDashBoardMain2(),
          getDashBoardMain3(),
          getDashBoardMain4(),
        ]);

      setDataAdmin1(resultAdmin1 || []);
      setDataAdmin2(resultAdmin2 || []);
      setDataAdmin3(resultAdmin3 || []);
      setDataAdmin4(resultAdmin4 || []);

      const chartdata2 = convertToChartData2(resultAdmin2);
      const chartdata4 = convertToChartData4(resultAdmin4);

      setChartDataAdmin2(chartdata2);
      setChartDataAdmin4(chartdata4);

      if (resultAdmin1 && resultAdmin1.length > 0) {
        const M_AVG_TOT = Math.max(
          ...resultAdmin1.map((item) => item.AVG_TOT || 0)
        );
        const M_SUPP_QTY = Math.max(
          ...resultAdmin1.map((item) => item.SUPP_QTY || 0)
        );

        setMaxAdminCow(M_AVG_TOT);
        setMaxAdminSupp(M_SUPP_QTY);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateYAxisDomain = (values, buffer = 100) => {
    const maxValue = Math.max(...values);
    return Math.ceil(maxValue / 100) * 100 + buffer;
  };

  const calculateAverage = (data, key) => {
    if (!data || data.length === 0) return "";
    const total = data.reduce((sum, item) => sum + (item[key] || 0), 0);
    const average = total / data.length;
    return average === 0 ? "" : average.toFixed(1);
  };

  const calculateAverageRate = (data) => {
    if (!data || data.length === 0) return "-";
    const total = data.reduce((sum, item) => {
      if (item.SUPP_QTY && item.COW_TOT) {
        return sum + item.SUPP_QTY / item.COW_TOT;
      }
      return sum;
    }, 0);
    const average = total / data.length;
    return average === 0 ? "-" : average.toFixed(1);
  };

  const convertToChartData2 = (data) => {
    if (!data) return [];
    return [
      { name: "1산", RAT: data.RAT_1 },
      { name: "2산", RAT: data.RAT_2 },
      { name: "3산", RAT: data.RAT_3 },
      { name: "4산", RAT: data.RAT_4 },
      { name: "5산↑", RAT: data.RAT_5 },
    ];
  };

  const convertToChartData4 = (data) => {
    if (!data) return [];
    return [
      { name: "15일↓", RAT: data.CALF_RAT1 },
      { name: "16일~30일", RAT: data.CALF_RAT2 },
      { name: "31일~45일", RAT: data.CALF_RAT3 },
      { name: "46일~60일", RAT: data.CALF_RAT4 },
      { name: "61일~75일", RAT: data.CALF_RAT5 },
      { name: "75일↑", RAT: data.CALF_RAT6 },
    ];
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A28DFF"];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, RAT } = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        >
          <p>{`${name} : ${RAT}%`}</p>
        </div>
      );
    }
    return null;
  };

  const chartDataAdmin2legendPayload = chartDataAdmin2.map((entry, index) => ({
    id: entry.name,
    type: "square", // 범례 마커 타입 설정
    value: entry.name, // 표시할 DIVNM 값
    color: COLORS[index % COLORS.length],
  }));

  const chartDataAdmin4legendPayload = chartDataAdmin4.map((entry, index) => ({
    id: entry.name,
    type: "square", // 범례 마커 타입 설정
    value: entry.name, // 표시할 DIVNM 값
    color: COLORS[index % COLORS.length],
  }));

  return (
    <>
      <SubTitle width="120px">주간 사육동향</SubTitle>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={dataAdmin1}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="YMD"
            tickFormatter={(value) => {
              if (value.length === 8) {
                const year = value.substring(0, 4); // 연도 추출
                const month = value.substring(4, 6); // 월 추출
                const day = value.substring(6, 8); // 일 추출
                return `${month}-${day}`; // "월-일" 형식 반환
              }
              return value; // 기본값 반환
            }}
            tick={{
              angle: -45,
              fontSize: 12,
              fill: "black",
            }}
            height={60}
            interval={0}
            tickMargin={22}
          />
          <YAxis
            yAxisId="right"
            domain={[25, Math.ceil(maxAdminCow / 10) * 10]}
            tickCount={8} // 눈금을 10개로 설정
            tickFormatter={(value) => value.toLocaleString()} // 숫자 포맷팅
            label={{
              value: "두당산유량",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 12, fill: "#333" },
            }}
          />
          <YAxis
            yAxisId="left"
            orientation="right"
            domain={[20000, Math.ceil(19000 / 10000) * 10000]}
            tickFormatter={(value) => value.toLocaleString()} // 숫자 포맷팅
            label={{
              value: "유량 (kg)",
              angle: -90,
              offset: 60,
              position: "insideLeft",
              style: { fontSize: 12, fill: "#333" },
            }}
          />
          <Tooltip
            labelFormatter={(value) => {
              if (value.length === 8) {
                const year = value.substring(0, 4);
                const month = value.substring(4, 6);
                const day = value.substring(6, 8);
                return `${year}-${month}-${day}`;
              }
              return value;
            }}
            formatter={(value, name) => {
              const formattedValue = value.toLocaleString();
              if (name === "AVG_TOT") return [formattedValue, "두당산유량"];
              if (name === "SUPP_QTY") return [formattedValue, "유량"];
              return [formattedValue, name];
            }}
          />
          <Legend
            formatter={(value) => {
              if (value === "AVG_TOT") return "두당산유량";
              if (value === "SUPP_QTY") return "유량";
              return value;
            }}
          />
          <Bar yAxisId="left" dataKey="SUPP_QTY" fill="#3498db" />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="AVG_TOT"
            stroke="#ef0505"
            strokeWidth={2}
          />
        </ComposedChart>
      </ResponsiveContainer>

      <TableContainer>
        <Table>
          <Tr>
            <Th rowSpan={2}>일자</Th>
            <Th colSpan={4}>사육두수</Th>
            <Th rowSpan={2}>납유량(L)</Th>
            <Th rowSpan={2}>
              두당
              <br />
              산유량(L)
            </Th>
          </Tr>
          <Tr>
            <Th>초임</Th>
            <Th>건유</Th>
            <Th>착유</Th>
            <Th>계</Th>
          </Tr>
          {/* data1 데이터를 기반으로 행 생성 */}
          {dataAdmin1 && dataAdmin1.length > 0 ? (
            dataAdmin1.map((item, index) => (
              <Tr key={index}>
                <Td textAlign="center">
                  {item.YMD
                    ? `${item.YMD.slice(4, 6)}-${item.YMD.slice(6, 8)}`
                    : "-"}
                </Td>
                <Td>{item.CNT_NONE?.toLocaleString() ?? ""}</Td>
                <Td>{item.CNT_DRY?.toLocaleString() ?? ""}</Td>
                <Td>{item.CNT_MILK?.toLocaleString() ?? ""}</Td>
                <Td>{item.CNT_TOT?.toLocaleString() ?? ""}</Td>
                <Td>{item.SUPP_QTY?.toLocaleString() ?? ""}</Td>
                <Td>
                  {item.COW_TOT && item.SUPP_QTY
                    ? (item.SUPP_QTY / item.COW_TOT).toFixed(1)
                    : ""}
                </Td>
              </Tr>
            ))
          ) : (
            <></>
          )}
          <Tr>
            <AvgTd textAlign="center">평균</AvgTd>
            <AvgTd>{calculateAverage(dataAdmin1, "CNT_NONE")}</AvgTd>
            <AvgTd>{calculateAverage(dataAdmin1, "CNT_DRY")}</AvgTd>
            <AvgTd>{calculateAverage(dataAdmin1, "CNT_MILK")}</AvgTd>
            <AvgTd>{calculateAverage(dataAdmin1, "COW_TOT")}</AvgTd>
            <AvgTd>{calculateAverage(dataAdmin1, "SUPP_QTY")}</AvgTd>
            <AvgTd>{calculateAverageRate(dataAdmin1)}</AvgTd>
          </Tr>
        </Table>
      </TableContainer>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <SubTitle width="120px">착유우 사육현황</SubTitle>
        <div style={{ marginTop: "10px", marginRight: "10px" }}>
          {yesterday.toLocaleDateString()} 기준
        </div>
      </div>

      <TableContainer>
        <Table>
          <Tr>
            <Th>구분</Th>
            <Th>산유량(kg)</Th>
            <Th>두수</Th>
            <Th>비율(%)</Th>
          </Tr>
          <Tr>
            <Th>1산</Th>
            <Td>{dataAdmin2 ? dataAdmin2.MILK_1.toFixed(1) : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.COW_1 : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.RAT_1 : ""}</Td>
          </Tr>
          <Tr>
            <Th>2산</Th>
            <Td>{dataAdmin2 ? dataAdmin2.MILK_2.toFixed(1) : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.COW_2 : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.RAT_2 : ""}</Td>
          </Tr>
          <Tr>
            <Th>3산</Th>
            <Td>{dataAdmin2 ? dataAdmin2.MILK_3.toFixed(1) : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.COW_3 : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.RAT_3 : ""}</Td>
          </Tr>
          <Tr>
            <Th>4산</Th>
            <Td>{dataAdmin2 ? dataAdmin2.MILK_4.toFixed(1) : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.COW_4 : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.RAT_4 : ""}</Td>
          </Tr>
          <Tr>
            <Th>5산↑</Th>
            <Td>{dataAdmin2 ? dataAdmin2.MILK_5.toFixed(1) : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.COW_5 : ""}</Td>
            <Td>{dataAdmin2 ? dataAdmin2.RAT_5 : ""}</Td>
          </Tr>
          <Tr>
            <SumTd textAlign="center">계</SumTd>
            <SumTd>{dataAdmin2 ? dataAdmin2.MILK_TOT : ""}</SumTd>
            <SumTd>{dataAdmin2 ? dataAdmin2.CNT_TOT : ""}</SumTd>
            <SumTd>{dataAdmin2 ? dataAdmin2.RAT_TOT : ""}</SumTd>
          </Tr>
        </Table>
      </TableContainer>

      <ResponsiveContainer height={350}>
        <PieChart>
          <Pie
            data={chartDataAdmin2}
            cx="50%"
            cy="50%"
            outerRadius={120}
            innerRadius={50}
            fill="#8884d8"
            dataKey="RAT"
            labelLine={true}
          >
            <LabelList
              dataKey="RAT"
              position="inside"
              style={{
                fill: "white",
                fontSize: "12px",
              }}
              formatter={(value) => `${value}%`}
            />
            {chartDataAdmin2.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend payload={chartDataAdmin2legendPayload} />
        </PieChart>
      </ResponsiveContainer>

      <TableContainer>
        <Table>
          <Tr>
            <Th>구분</Th>
            <Th>산유량(kg)</Th>
            <Th>두수</Th>
            <Th>비율(%)</Th>
          </Tr>
          <Tr>
            <Th>20kg ↓</Th>
            <Td>{dataAdmin3 ? dataAdmin3.MILK_20.toFixed(1) : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.COW_20 : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.RAT_20 : ""}</Td>
          </Tr>
          <Tr>
            <Th>21~25kg</Th>
            <Td>{dataAdmin3 ? dataAdmin3.MILK_21.toFixed(1) : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.COW_21 : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.RAT_21 : ""}</Td>
          </Tr>
          <Tr>
            <Th>26~30kg</Th>
            <Td>{dataAdmin3 ? dataAdmin3.MILK_26.toFixed(1) : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.COW_26 : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.RAT_26 : ""}</Td>
          </Tr>
          <Tr>
            <Th>31~35kg</Th>
            <Td>{dataAdmin3 ? dataAdmin3.MILK_31.toFixed(1) : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.COW_31 : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.RAT_31 : ""}</Td>
          </Tr>
          <Tr>
            <Th>36~40kg</Th>
            <Td>{dataAdmin3 ? dataAdmin3.MILK_36.toFixed(1) : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.COW_36 : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.RAT_36 : ""}</Td>
          </Tr>
          <Tr>
            <Th>40kg ↑</Th>
            <Td>{dataAdmin3 ? dataAdmin3.MILK_40.toFixed(1) : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.COW_40 : ""}</Td>
            <Td>{dataAdmin3 ? dataAdmin3.RAT_40 : ""}</Td>
          </Tr>
        </Table>
      </TableContainer>

      <SubTitle width="140px">송아지 사육 현황</SubTitle>

      <ResponsiveContainer height={350}>
        <PieChart>
          <Pie
            data={chartDataAdmin4}
            cx="50%"
            cy="50%"
            outerRadius={120}
            innerRadius={50}
            fill="#8884d8"
            dataKey="RAT"
            labelLine={true}
          >
            <LabelList
              dataKey="RAT"
              position="inside"
              style={{
                fill: "white",
                fontSize: "12px",
              }}
              formatter={(value) => `${value}%`}
            />
            {chartDataAdmin4.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend payload={chartDataAdmin4legendPayload} />
        </PieChart>
      </ResponsiveContainer>
    </>
  );
};
export default AdminHome;
